"use client"

import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Menu } from 'lucide-react'
import { marked } from "marked"
import CompanyNameSearch from "./CompanyNameSearch"
import RelatedWordSearch from "./RelatedWordSearch"
import IndustrySearch from "./IndustrySearch"
import TanshinSearch from "./TanshinSearch"
import NewReleaseSearch from "./NewReleaseSearch"
import api from "../api/api"

import "./css/Analysis.css"

const AnalysisTabContent = ({
  selectedCompanies,
  onCompanyClick,
  onDashboardClick,
  addAllCompanies,
  companyDescription,
  setCompanyDescription,
  handleOpenAnalysis,
  analysisAreaOpend,
  handleSummaryClick,
  searchAreaWidth,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  // 現在表示するタブを管理するstate
  const [activeTab, setActiveTab] = useState("CompanyNameSearch")
  const [industryData, setIndustryData] = useState(null)
  const [tanshinData, setTanshinData] = useState([])
  const [nextPage, setNextPage] = useState(null)
  const [prevPage, setPrevPage] = useState(null)
  const [registeredCompanies, setRegisteredCompanies] = useState([])
  const [showCompanyDescription, setShowCompanyDescription] = useState(false)
  const [descriptionSecCode, setDescriptionSecCode] = useState(false)
  const [tanshinCountry, setTanshinCountry] = useState("JP")
  const [tanshinPeriod, setTanshinPeriod] = useState("all")
  const [isMobile, setIsMobile] = useState(false)

  // モバイル判定
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    if (searchAreaWidth) {
      setIsMobile(searchAreaWidth <= 850)
    }
  }, [searchAreaWidth])

  // タブの切り替え
  const openTab = (tabName) => {
    setActiveTab(tabName) // クリックされたタブ名に変更
  }

  const fetchTanshinData = async () => {
    try {
      const pageUrl = `${process.env.REACT_APP_API_URL}/chat/api/tanshin-list/`
      const url = new URL(pageUrl)
      url.searchParams.set("country", tanshinCountry)
      url.searchParams.set("period", tanshinPeriod)
      const response = await api.get(url.toString())
      setTanshinData(response.data.results) // ページネーションされたデータ
      setNextPage(response.data.next) // 次のページURL
      setPrevPage(response.data.previous) // 前のページURL
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        navigate("/login", { state: { from: location.pathname } })
      } else {
        console.error("Error fetching financial results:", error)
      }
    }
  }

  const fetchRegisteredCompanies = async (
    pageUrl = `${process.env.REACT_APP_API_URL}/chat/api/registered-companies/`,
  ) => {
    try {
      const response = await api.get(pageUrl)
      setRegisteredCompanies(response.data)
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        navigate("/login", { state: { from: location.pathname } })
      } else {
        console.error("Error fetching financial results:", error)
      }
    }
  }

  // 業界データ・短信データの取得
  useEffect(() => {
    const fetchIndustryData = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/industry/`)
        setIndustryData(response.data)
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          navigate("/login", { state: { from: location.pathname } })
        } else {
          console.error("Error fetching financial results:", error)
        }
      }
    }
    fetchIndustryData()
    fetchTanshinData()
    // fetchRegisteredCompanies();
  }, [])

  const handleNextPage = () => {
    if (nextPage) {
      fetchTanshinData(nextPage)
    }
  }

  const handlePrevPage = () => {
    if (prevPage) {
      fetchTanshinData(prevPage)
    }
  }

  const addCompany = async (secCode) => {
    try {
      // APIリクエストで会社を追加
      const response = await api.post(`${process.env.REACT_APP_API_URL}/chat/api/registered-companies/`, {
        company_name: secCode,
      })
      setRegisteredCompanies((prev) => [...prev, response.data])
    } catch (error) {
      console.error(`Error adding company with sec_code ${secCode}:`, error)
    }
  }

  const removeCompany = async (secCode) => {
    try {
      // APIリクエストでsecCodeを使って会社を削除
      await api.delete(`${process.env.REACT_APP_API_URL}/chat/api/registered-companies/`, {
        data: { sec_code: secCode }, // DELETEリクエストのボディにsec_codeを渡す
      })
      setRegisteredCompanies((prev) => prev.filter((company) => company.sec_code !== secCode))
    } catch (error) {
      console.error(`Error removing company with sec_code ${secCode}:`, error)
    }
  }

  const handleDescriptionClick = (secCode) => {
    setDescriptionSecCode(secCode)
    setShowCompanyDescription(true)
  }

  const handleCloseClick = () => {
    setShowCompanyDescription(false)
  }

  const handleTanshinCountrySelect = (selectedCountry) => {
    setTanshinCountry(selectedCountry)
  }

  useEffect(() => {
    fetchTanshinData()
  }, [tanshinCountry, tanshinPeriod])

  // モバイル用のタブレンダリング
  const renderMobileTabs = () => (
    <div className="flex overflow-x-auto whitespace-nowrap py-2 px-1 bg-white border-b text-sm sticky top-0 z-10">
      <button
        className={`px-3 py-1.5 rounded-md mr-2 font-medium ${activeTab === "CompanyNameSearch" ? "bg-gray-100 text-gray-800" : "text-gray-600"}`}
        onClick={() => openTab("CompanyNameSearch")}
      >
        企業名検索
      </button>
      <button
        className={`px-3 py-1.5 rounded-md mr-2 font-medium ${activeTab === "RelatedWordSearch" ? "bg-gray-100 text-gray-800" : "text-gray-600"}`}
        onClick={() => openTab("RelatedWordSearch")}
      >
        関連ワード検索
      </button>
      <button
        className={`px-3 py-1.5 rounded-md mr-2 font-medium ${activeTab === "IndustrySearch" ? "bg-gray-100 text-gray-800" : "text-gray-600"}`}
        onClick={() => openTab("IndustrySearch")}
      >
        業界検索
      </button>
      <button
        className={`px-3 py-1.5 rounded-md mr-2 font-medium ${activeTab === "TanshinSearch" ? "bg-gray-100 text-gray-800" : "text-gray-600"}`}
        onClick={() => openTab("TanshinSearch")}
      >
        新着決算から検索
      </button>
      <button
        className={`px-3 py-1.5 rounded-md font-medium ${activeTab === "NewReleaseSearch" ? "bg-gray-100 text-gray-800" : "text-gray-600"}`}
        onClick={() => openTab("NewReleaseSearch")}
      >
        新着リリースから検索
      </button>
    </div>
  )

  // デスクトップ用のタブレンダリング（既存のクラスを維持）
  const renderDesktopTabs = () => (
    <div className="tab-search">
      <button
        className={`tablinks ${activeTab === "CompanyNameSearch" ? "active" : ""}`}
        onClick={() => openTab("CompanyNameSearch")}
      >
        企業名検索
      </button>
      <button
        className={`tablinks ${activeTab === "RelatedWordSearch" ? "active" : ""}`}
        onClick={() => openTab("RelatedWordSearch")}
      >
        関連ワード検索
      </button>
      <button
        className={`tablinks ${activeTab === "IndustrySearch" ? "active" : ""}`}
        onClick={() => openTab("IndustrySearch")}
      >
        業界検索
      </button>
      <button
        className={`tablinks ${activeTab === "TanshinSearch" ? "active" : ""}`}
        onClick={() => openTab("TanshinSearch")}
      >
        新着決算から検索
      </button>
      <button
        className={`tablinks ${activeTab === "NewReleaseSearch" ? "active" : ""}`}
        onClick={() => openTab("NewReleaseSearch")}
      >
        新着リリースから検索
      </button>
    </div>
  )

  return (
    <div className={isMobile ? "flex flex-col h-full" : "h-full flex flex-col"}>
      {/* 画面サイズに応じてタブを切り替え */}
      {isMobile ? renderMobileTabs() : renderDesktopTabs()}
      {/* コンテンツエリア - スクロール可能 */}
      <div className={isMobile ? "flex-1 overflow-y-auto bg-white mt-2 rounded-md scrollbar-hide" : "h-[calc(100%-37px)]"}>
        {/* 各タブの表示をactiveTabに応じて切り替える */}
        {activeTab === "CompanyNameSearch" && (
          <CompanyNameSearch
            onDashboardClick={onDashboardClick}
            selectedCompanies={selectedCompanies}
            onCompanyClick={onCompanyClick}
            addAllCompanies={addAllCompanies}
            addCompany={addCompany}
            removeCompany={removeCompany}
            registeredCompanies={registeredCompanies}
            setCompanyDescription={setCompanyDescription}
            handleOpenAnalysis={handleOpenAnalysis}
            handleDescriptionClick={handleDescriptionClick}
            isMobile={isMobile}
          />
        )}
        {activeTab === "RelatedWordSearch" && (
          <RelatedWordSearch
            onDashboardClick={onDashboardClick}
            selectedCompanies={selectedCompanies}
            onCompanyClick={onCompanyClick}
            addAllCompanies={addAllCompanies}
            addCompany={addCompany}
            removeCompany={removeCompany}
            registeredCompanies={registeredCompanies}
            setCompanyDescription={setCompanyDescription}
            handleOpenAnalysis={handleOpenAnalysis}
            analysisAreaOpend={analysisAreaOpend}
            isMobile={isMobile}
          />
        )}
        {activeTab === "IndustrySearch" && (
          <IndustrySearch
            onDashboardClick={onDashboardClick}
            selectedCompanies={selectedCompanies}
            onCompanyClick={onCompanyClick}
            industryData={industryData}
            addAllCompanies={addAllCompanies}
            addCompany={addCompany}
            removeCompany={removeCompany}
            registeredCompanies={registeredCompanies}
            setCompanyDescription={setCompanyDescription}
            handleOpenAnalysis={handleOpenAnalysis}
            analysisAreaOpend={analysisAreaOpend}
            isMobile={isMobile}
          />
        )}
        {activeTab === "TanshinSearch" && (
          <TanshinSearch
            tanshinData={tanshinData}
            onDashboardClick={onDashboardClick}
            selectedCompanies={selectedCompanies}
            onCompanyClick={onCompanyClick}
            addAllCompanies={addAllCompanies}
            addCompany={addCompany}
            removeCompany={removeCompany}
            registeredCompanies={registeredCompanies}
            handleOpenAnalysis={handleOpenAnalysis}
            handleTanshinCountrySelect={handleTanshinCountrySelect}
            tanshinCountry={tanshinCountry}
            selectedPeriod={tanshinPeriod}
            setSelectedPeriod={setTanshinPeriod}
            isMobile={isMobile}
          />
        )}
        {activeTab === "NewReleaseSearch" && (
          <NewReleaseSearch
            onDashboardClick={onDashboardClick}
            selectedCompanies={selectedCompanies}
            onCompanyClick={onCompanyClick}
            addAllCompanies={addAllCompanies}
            addCompany={addCompany}
            removeCompany={removeCompany}
            registeredCompanies={registeredCompanies}
            handleOpenAnalysis={handleOpenAnalysis}
            handleSummaryClick={handleSummaryClick}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  )
}

export default AnalysisTabContent

